/** @jsx jsx */
import { jsx, css } from '@emotion/core';

import React, { Fragment } from 'react';
import { Box, Typography } from '@material-ui/core';
import { Link } from '@reach/router';
import { ChevronRight } from '@material-ui/icons';

import {
  RequestStatus,
  Request,
  RequestCategory,
  ContactMethod,
  ContactStatus,
  StatusReason,
} from '../../../__generated__/pgeplus-types';

import { PageTextContentType } from '../../../hooks/useContentMgmt';
import { useIsMobile } from '../../../util/style-utils';
import { STEP_STATUS } from './welcomeBack.types';
import { formatDate } from '../../../util/format';
import theme from '../../../themes/theme';
import ROUTES from '../../../routes';

import { WelcomeBackStep } from './common/Step';
import WelcomeBackHeader from './common/Header';
import ApplicationInprogress from './common/steps/ApplicationInprogress';
import { WelcomeBackStepHeader } from './common/StepHeader';
import { WelcomeBackStepDetails } from './common/StepDetails';
import { WelcomeBackSubHeading } from './EVApplication';
import { getFormattedDateFromUTCSting } from '../../../util/date-utils';

type Props = {
  application: Request;
  content: PageTextContentType;
  activeStep: number;
  isClosed?: boolean;
};

const WelcomeBackConsultationRequest = ({
  application,
  content,
  activeStep,
  isClosed,
}: Props) => {
  const isMobile = useIsMobile();

  const contactMethodOption = {
    [ContactMethod.Phone]: content.get('REUSABLE_PHONE') || '',
    [ContactMethod.Email]: content.get('REUSABLE_EMAIL') || '',
  };

  const cancellationReason = {
    [StatusReason.CustomerNotReached]:
      content.get('OPTION_CUSTOMER_COULD_NOT_BE_REACHED') || '',
    [StatusReason.DuplicateRequest]:
      content.get('OPTION_DUPLICATED_REQUEST') || '',
    [StatusReason.CustomerOptedOut]:
      content.get('OPTION_CUSTOMER_NO_LONGER_WANTS_TO_BE_CONTACTED') || '',
    [StatusReason.Other]: content.get('OPTION_OTHER') || '',
  };

  const contactAttempts =
    application.status === RequestStatus.InProgress
      ? application?.contactAttempts?.filter(
          contactAttempt => contactAttempt?.status !== ContactStatus.Successful,
        )
      : application?.contactAttempts;

  const getStepStatus = (step: number) => {
    if (activeStep === step) {
      return STEP_STATUS.IN_PROGRESS;
    }
    if (step > activeStep) {
      return STEP_STATUS.PENDING;
    }

    return STEP_STATUS.COMPLETED;
  };

  const getStepText = (step: number) => {
    if (step === 1) {
      return {
        heading: content.get('YOUR_CONSULTATION_REQUEST_HAS_BEEN_SUBMITTED'),
        subHeading: (
          <Link
            to={ROUTES.PGE_PLUS_VIEW_CONSULTATION_APPLICATION_SUMMARY}
            state={{ requestId: application.id, showContactSummary: false }}
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              textDecoration: 'none',
              fontSize: 'inherit',
            }}
          >
            {content.get('VIEW_COMPLETED_AND_SUBMITTED_ANSWERS')}
            <ChevronRight />
          </Link>
        ),
      };
    }
    if (step === 2) {
      if (
        application.status === RequestStatus.Created ||
        (application.status === RequestStatus.InProgress &&
          !Boolean(
            application?.contactAttempts?.filter(
              contactAttempt =>
                contactAttempt?.status !== ContactStatus.Successful,
            )?.length,
          ))
      ) {
        return {
          heading: content.get('A_PGE_SPECIALIST_WILL_CONTACT_YOU_SOON'),
          subHeading: content.get('ONE_OF_OUR_EXPERTS_WILL_REACH_OUT_TO_YOU'),
        };
      }
      if (application.status === RequestStatus.Completed) {
        return {
          heading: content.get('A_PGE_SPECIALIST_REACHED_OUT_ON'),
        };
      }
      if (
        [RequestStatus.InProgress, RequestStatus.Canceled].includes(
          application.status,
        )
      ) {
        return {
          heading: content.get('A_PGE_SPECIALIST_TRIED_TO_REACH_OUT'),
        };
      }
    }
    if (step === 3) {
      if (
        [
          RequestStatus.Created,
          RequestStatus.InProgress,
          RequestStatus.Completed,
        ].includes(application.status)
      ) {
        return {
          heading: content.get('CONSULTATION_COMPLETED'),
          subHeading: application.status === RequestStatus.Completed && (
            <Link
              to={ROUTES.PGE_PLUS_VIEW_CONSULTATION_APPLICATION_SUMMARY}
              state={{ requestId: application.id, showContactSummary: true }}
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                textDecoration: 'none',
                fontSize: 'inherit',
              }}
            >
              {content.get('VIEW_YOUR_CONSULTATION_SUMMARY')}
              <ChevronRight />
            </Link>
          ),
        };
      } else if (application.status === RequestStatus.Canceled) {
        return {
          heading: content.get('CONSULTATION_CANCELLED'),
          subHeading: application?.statusReason ? (
            <Fragment>
              {content.get('REASON_FOR_CANCELLATION')}:&nbsp;
              {application.statusReason === StatusReason.Other ? (
                <Fragment>
                  {cancellationReason[application.statusReason]}
                  <br />
                  <Typography css={{ marginTop: 10 }}>
                    {application.statusNotes}
                  </Typography>
                </Fragment>
              ) : (
                cancellationReason[application.statusReason] || ''
              )}
            </Fragment>
          ) : (
            ''
          ),
        };
      }
    }
  };

  const AppDetails = (
    <Fragment>
      <WelcomeBackStep
        step={1}
        status={getStepStatus(1)}
        isClosed={isClosed || application?.status === RequestStatus.Canceled}
      >
        {getStepStatus(1) === STEP_STATUS.COMPLETED && (
          <React.Fragment>
            <WelcomeBackStepHeader title={getStepText(1)?.heading!} />
            <WelcomeBackStepDetails>
              <WelcomeBackSubHeading subHeading={getStepText(1)?.subHeading} />
            </WelcomeBackStepDetails>
          </React.Fragment>
        )}
        <Box css={{ margin: '10px 0' }}></Box>
        <ApplicationInprogress status={getStepStatus(1)} content={content} />
      </WelcomeBackStep>

      <WelcomeBackStep
        step={2}
        status={getStepStatus(2)}
        isClosed={isClosed || application?.status === RequestStatus.Canceled}
      >
        <WelcomeBackStepHeader title={getStepText(2)?.heading!} />
        <WelcomeBackStepDetails>
          <WelcomeBackSubHeading subHeading={getStepText(2)?.subHeading} />
          {contactAttempts?.map((attempt, index) => {
            return (
              <Box key={index} css={{ marginBottom: 15 }}>
                <Typography>
                  {getFormattedDateFromUTCSting(
                    attempt?.contactDate,
                    'MM/dd/yyyy',
                  )}
                </Typography>
                <Typography>
                  {attempt?.contactMethod
                    ?.map(method => contactMethodOption[method])
                    .join(', ') || ''}
                </Typography>
              </Box>
            );
          })}
          {(contactAttempts || []).length >= 1 &&
            (contactAttempts || []).length <= 2 &&
            ![RequestStatus.Completed, RequestStatus.Canceled].includes(
              application.status,
            ) && (
              <Typography>
                {content.get('WE_WILL_TRY_TO_CONTACT_YOU_AGAIN')}
              </Typography>
            )}
        </WelcomeBackStepDetails>
      </WelcomeBackStep>

      <WelcomeBackStep
        step={3}
        status={
          getStepStatus(3) === STEP_STATUS.COMPLETED
            ? STEP_STATUS.COMPLETED
            : STEP_STATUS.PENDING
        }
        isClosed={isClosed || application?.status === RequestStatus.Canceled}
      >
        <WelcomeBackStepHeader title={getStepText(3)?.heading!} />
        <WelcomeBackStepDetails>
          <WelcomeBackSubHeading subHeading={getStepText(3)?.subHeading} />
        </WelcomeBackStepDetails>
      </WelcomeBackStep>
    </Fragment>
  );

  return (
    <Fragment>
      <Box css={{ marginBottom: theme.spacing(4) }}>
        {!(application.status === RequestStatus.Canceled) && (
          <WelcomeBackHeader
            applicationId={application.id}
            title={
              application.category === RequestCategory.HvacConsultation
                ? content.get('WELCOME_BACK_HVAC_CONSULTATION_HEADER')!
                : content.get('WELCOME_BACK_EV_CONSULTATION_HEADER')!
            }
            appNumber={
              <Box
                gridGap={2}
                display={'flex'}
                flexDirection={isMobile ? 'column' : 'row'}
              >
                <Typography component="span">
                  {content.get('REQUEST_NO')}
                </Typography>{' '}
                <Typography component="span" style={{ fontWeight: 700 }}>
                  {application?.requestNo}
                </Typography>
              </Box>
            }
            appDate={
              <Box
                display={'flex'}
                flexDirection={isMobile ? 'column' : 'row'}
                alignItems={'flex-end'}
              >
                <Typography component="span">
                  {content.get('REQUEST_SUBMISSION_DATE')}
                </Typography>
                &nbsp;
                <Typography component="span" style={{ fontWeight: 700 }}>
                  {formatDate(new Date(application?.createdAt))}
                </Typography>
              </Box>
            }
            isConsultationRequest={true}
          />
        )}
        {AppDetails}
      </Box>
    </Fragment>
  );
};

export default WelcomeBackConsultationRequest;
